import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Download, FileText, FileSpreadsheet, BookOpen, Clock, MessageCircle, Eye, EyeOff } from 'lucide-react';

const TabsControlBar = ({
  activeTab, handleTabChange,
  fyOptions, activeFY, handleFYChange,
  showCustomRange, setShowCustomRange,
  customStart, setCustomStart,
  customEnd, setCustomEnd,
  applyCustomRange,
  report,
  openDownloadDialog,
  downloadStatement,
  shareWhatsApp,
  isSuperAdminUser,
  challanTabVisible,
  challanVisLoading,
  toggleChallanTab,
}) => {
  const tabs = [
    { key: 'invoice', label: 'Invoice Commission', icon: FileText },
    ...(challanTabVisible ? [{ key: 'challan', label: 'Challan', icon: FileSpreadsheet }] : []),
    { key: 'ledger', label: 'Payment Ledger', icon: BookOpen },
    { key: 'aging', label: 'Aging Report', icon: Clock },
  ];

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        {/* Tabs */}
        <div className="flex gap-1 border-b pb-3 mb-1 items-center">
          {tabs.map(t => (
            <button key={t.key} onClick={() => handleTabChange(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === t.key ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              data-testid={`tab-${t.key}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
          {isSuperAdminUser && (
            <div className="ml-auto flex items-center gap-2 pl-4 border-l border-gray-200" data-testid="challan-tab-toggle">
              <span className="text-xs text-gray-500">{challanTabVisible ? <Eye className="w-3.5 h-3.5 inline" /> : <EyeOff className="w-3.5 h-3.5 inline" />} Challan Tab</span>
              <Switch checked={challanTabVisible} disabled={challanVisLoading} onCheckedChange={toggleChallanTab} data-testid="toggle-challan-tab" />
            </div>
          )}
        </div>
        {/* Period selector (FY presets + custom range) — applies to all tabs */}
        <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-wrap gap-1 items-center">
              {(activeTab === 'ledger' || activeTab === 'aging') && (
                <span className="text-xs font-medium text-gray-500 mr-1">Period:</span>
              )}
              {fyOptions.map(fy => (
                <Button key={fy.label} size="sm"
                  variant={activeFY.label === fy.label ? 'default' : 'outline'}
                  className={activeFY.label === fy.label ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                  onClick={() => handleFYChange(fy)} data-testid={`fy-btn-${fy.label}`}>
                  {fy.label}
                </Button>
              ))}
              <Button size="sm"
                variant={activeFY.isCustom || showCustomRange ? 'default' : 'outline'}
                className={activeFY.isCustom || showCustomRange ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                onClick={() => setShowCustomRange(v => !v)} data-testid="fy-btn-custom">
                Custom
              </Button>
              {showCustomRange && (
                <div className="flex items-center gap-2 ml-2 p-2 bg-gray-50 rounded-md border" data-testid="custom-range-picker">
                  <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                    className="h-8 w-40 text-xs" data-testid="custom-start-date" />
                  <span className="text-xs text-gray-500">to</span>
                  <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                    className="h-8 w-40 text-xs" data-testid="custom-end-date" />
                  <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" onClick={applyCustomRange} data-testid="apply-custom-range-btn">Apply</Button>
                </div>
              )}
              {activeFY.isCustom && !showCustomRange && (
                <span className="ml-2 text-xs text-gray-600 italic">{activeFY.label}</span>
              )}
            </div>
            {(activeTab === 'invoice' || activeTab === 'challan') && report && (
              <div className="flex gap-1 ml-auto">
                <Button size="sm" className="gap-1 bg-indigo-600 hover:bg-indigo-700" onClick={openDownloadDialog} data-testid="open-download-dialog-btn"><Download className="w-4 h-4" /> Download Report</Button>
                <Button size="sm" variant="outline" className="gap-1" onClick={downloadStatement} data-testid="download-statement-btn"><FileText className="w-4 h-4" /> Statement</Button>
                <Button size="sm" className="gap-1 bg-green-600 hover:bg-green-700" onClick={shareWhatsApp} data-testid="share-whatsapp-btn"><MessageCircle className="w-4 h-4" /> WhatsApp</Button>
              </div>
            )}
          </div>
      </CardContent>
    </Card>
  );
};

export default TabsControlBar;
